import { CloudUploadOutlined, UploadOutlined } from "@ant-design/icons";
import { Button, Form, Upload, UploadFile } from "antd";
import {
  Control,
  Controller,
  FieldValues,
  Path,
  ControllerRenderProps,
} from "react-hook-form";
import { ReactNode, useCallback, useRef } from "react";

interface Props<TSchema extends FieldValues> {
  name: Path<TSchema>;
  label: ReactNode;
  placeholder: string;
  errorMessage?: string;
  control: Control<TSchema>;
  listType: "text" | "picture" | "picture-card";
}
import styles from "./FieldUpload.module.scss";

export default function FieldUpload<TSchema extends FieldValues>(
  props: Props<TSchema>,
) {
  const { name, errorMessage, control, listType } = props;

  const inputFileRef = useRef<HTMLInputElement | null>(null);

  const renderField = useCallback(
    ({ field }: { field: ControllerRenderProps<TSchema, Path<TSchema>> }) => {
      const fileList: UploadFile[] = Array.isArray(field.value)
        ? field.value
        : [];

      // Files selector via hidden input
      const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files) {
          const newFileList = Array.from(files).map((file) => file);
          field.onChange([...fileList, ...newFileList]);
        }
      };

      const onClick = () => inputFileRef.current?.click();

      const onRemove = (file: UploadFile) => {
        const updatedFileList = fileList.filter(
          (item) => item.uid !== file.uid,
        );
        field.onChange(updatedFileList);
      };

      return (
        <>
          <div className={styles.uploadContainer}>
            <CloudUploadOutlined className={styles.uploadIcon} />
            <div className={styles.uploadDescription}>
              <span>SÃ©lectionnez un fichier ou faites le glisser ici</span>
              <span>JPG, PNG ou PDF, taille maximum de 3MB</span>
            </div>
            <Button
              type="default"
              className={`download-btn ${styles.uploadButton}`}
              icon={<UploadOutlined />}
              onClick={onClick} // Open file selector
            >
              TÃLÃCHARGER
            </Button>
          </div>
          {/* Hidden input file to handle files */}
          <input
            type="file"
            ref={inputFileRef}
            style={{ display: "none" }}
            accept=".png, .jpeg, .jpg, .pdf"
            multiple
            onChange={handleFileChange}
          />
          {/* Show files list */}
          <Upload
            fileList={fileList}
            onRemove={onRemove}
            listType={listType}
            showUploadList
          />
        </>
      );
    },
    [listType],
  );

  return (
    <Form.Item
      validateStatus={errorMessage ? "error" : ""}
      help={errorMessage || null}
      className={styles.root}
    >
      <Controller control={control} name={name} render={renderField} />
    </Form.Item>
  );
}
